import { useEffect, useRef, useState } from "react";
import { ChevronDown, User, Users } from "lucide-react";

/**
 * Segmented control: Mine | Team | By BDR ▾
 * value shape: { scope: 'mine'|'team'|'user', user_id?: string }
 */
export const ScopeToggle = ({ value, onChange, users = [], currentUserName }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isMine = value.scope === "mine";
  const isTeam = value.scope === "team";
  const isByBdr = value.scope === "user";

  const pickUser = (u) => {
    onChange({ scope: "user", user_id: u.user_id, user_name: u.name });
    setDropdownOpen(false);
  };

  const selectedBdrLabel = isByBdr ? (value.user_name || "BDR") : "By BDR";

  const baseBtn = "h-9 px-3 text-xs font-mono-data tracking-wider uppercase transition-colors border flex items-center gap-2";
  const active = "bg-[#0A84FF] text-white border-[#0A84FF]";
  const inactive = "bg-transparent text-[#A3A3A3] border-[#262626] hover:text-white hover:border-[#404040]";

  return (
    <div className="inline-flex items-stretch rounded-sm overflow-hidden" data-testid="scope-toggle">
      <button
        type="button"
        onClick={() => onChange({ scope: "mine" })}
        data-testid="scope-mine"
        className={`${baseBtn} rounded-l-sm border-r-0 ${isMine ? active : inactive}`}
        title={currentUserName ? `Filter to ${currentUserName}` : "Filter to my activity"}
      >
        <User className="w-3 h-3" strokeWidth={1.8}/>
        <span className="hidden sm:inline">Mine</span>
      </button>
      <button
        type="button"
        onClick={() => onChange({ scope: "team" })}
        data-testid="scope-team"
        className={`${baseBtn} border-r-0 ${isTeam ? active : inactive}`}
      >
        <Users className="w-3 h-3" strokeWidth={1.8}/>
        <span className="hidden sm:inline">Team</span>
      </button>
      <div className="relative" ref={wrapperRef}>
        <button
          type="button"
          onClick={() => setDropdownOpen((v) => !v)}
          data-testid="scope-by-bdr"
          className={`${baseBtn} rounded-r-sm ${isByBdr ? active : inactive}`}
        >
          <span className="truncate max-w-[120px]">{selectedBdrLabel}</span>
          <ChevronDown className="w-3 h-3" strokeWidth={1.8}/>
        </button>
        {dropdownOpen && (
          <div
            className="absolute right-0 top-10 min-w-[220px] bg-[#141414] border border-[#262626] rounded-sm shadow-xl overflow-hidden z-50 max-h-80 overflow-y-auto"
            data-testid="scope-bdr-dropdown"
          >
            {users.length === 0 ? (
              <div className="px-3 py-3 text-sm text-[#737373]">No users yet</div>
            ) : users.map((u) => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => pickUser(u)}
                data-testid={`scope-user-option-${u.user_id}`}
                className={`w-full text-left px-3 py-2 border-b border-[#1F1F1F] last:border-b-0 hover:bg-[#1F1F1F] transition-colors ${
                  isByBdr && value.user_id === u.user_id ? "bg-[#1F1F1F]" : ""
                }`}
              >
                <div className="text-sm text-white truncate">{u.name}</div>
                <div className="text-[11px] text-[#737373] font-mono-data truncate">{u.email}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScopeToggle;
